/* eslint-disable react/no-danger */
import { Button, Collapse, Modal, Text } from '@nextui-org/react'
import DOMPurify from 'dompurify'
import {
  ArrowRight,
  GraduationCap,
  Money,
  NewspaperClipping,
  Suitcase,
  WifiHigh,
} from 'phosphor-react'
import { bool, shape, string } from 'prop-types'
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TiStarFullOutline } from 'react-icons/ti'
import { useQuery } from 'react-query'
import { FeathersContext } from '../../../../services/feathers'
import { Likecontext } from '../../../../services/likeContext'
import {
  experienceOptions,
  remote,
  schoolLevelsI18,
} from '../../../../utils/utils'
import OfferInformation from '../OfferInformation/OfferInformation'
import * as s from './Card.style'

const propTypes = {
  job: shape({}),
  isTop: bool,
  superLike: bool,
  companyName: string,
  fromApplicationTab: bool,
}

const defaultProps = {
  superLike: false,
  companyName: null,
}

// forwardRef allow us to keep references we pass from parent
// when using function components
// https://reactjs.org/docs/forwarding-refs.html
const CustomCard = ({
  job,
  isTop,
  superLike,
  companyName,
  fromApplicationTab,
  children,
}) => {
  const likeContext = useContext(Likecontext)
  const feathers = useContext(FeathersContext)
  const { t } = useTranslation(['common', 'talentArea', 'campaign'], {
    useSuspense: false,
  })
  const createMarkup = (html) => ({
    __html: DOMPurify.sanitize(html),
  })

  const [direction, setDirection] = useState(null)
  const [rotation, setRotation] = useState(null)

  const { data } = useQuery({
    queryKey: ['company', job.company_id],
    queryFn: async () => {
      const result = await feathers.service('lambda-talent').find({
        query: {
          type: 'getCompany',
          companyId: job.company_id,
        },
      })
      return result
    },
  })

  const [visible, setVisible] = useState(false)
  const handler = () => setVisible(true)
  const closeHandler = () => {
    setVisible(false)
  }

  useEffect(() => {
    if (likeContext?.value === 'rejected') {
      setDirection(-500)
      setRotation('-10deg')
    }
    if (likeContext?.value === 'approved') {
      setDirection(500)
      setRotation('10deg')
    }
  }, [likeContext?.value])
  // superLike
  return (
    <s.CardWrapper
      variant="bordered"
      style={{
        maxWidth: '736px',
        margin: '50px auto',
        borderRadius: '16px',
        border: '1px solid #DAE1FA',
      }}
      className="card"
    >
      <header>
        <div
          className="card__header"
          style={{
            backgroundColor: '#E5E6EB',
            color: '#27345E',
            padding: '1.5rem 3rem',
            borderTopLeftRadius: '16px',
            borderTopRightRadius: '16px',
          }}
        >
          <div
            className="company-infos-card"
            style={{ display: 'flex', gap: 10, alignItems: 'center' }}
          >
            {data?.standalone?.topLogoId && (
              <img
                src={data.standalone.topLogoId}
                alt="logo company"
                width={80}
              />
            )}
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {data?.name?.length && <h2 className="job-title">{data.name}</h2>}
              <OfferInformation offer={job} fontSize="1.5em" />
            </div>
          </div>
          <h2 className="job-title">
            {superLike && !fromApplicationTab && (
              <div style={{ position: 'absolute', top: 0, right: 0 }}>
                <span className="like-desc">
                  {t('talentArea:likes.superLine.desc')}
                </span>
                <TiStarFullOutline color="#ff5722" size={44} />
              </div>
            )}{' '}
          </h2>
        </div>
      </header>
      <div className="card-body">
        <div>
          <p className="jobname">{job?.displayName || job?.name}</p>
          <div
            className="job-description"
            style={{
              display: 'flex',
              justifyContent: 'flex-start',
              marginTop: '20px',
            }}
          >
            <div style={{ marginRight: '160px' }}>
              <h3 className="job-info-title">Infos sur le poste</h3>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  fontSize: '16px',
                  color: '#2B3E7C',
                }}
              >
                <span>
                  <NewspaperClipping size={18} style={{ marginRight: 8 }} />
                  {job.contractType}
                </span>
                <span>
                  <Money size={18} style={{ marginRight: 8 }} />
                  {job?.salaryRange?.minValue}K - {job?.salaryRange?.maxValue}K
                </span>

                {job.remoteWorking && (
                  <span>
                    <WifiHigh size={18} style={{ marginRight: 8 }} />
                    <span
                      dangerouslySetInnerHTML={createMarkup(
                        remote(job.remoteWorking, t),
                      )}
                    />
                  </span>
                )}
              </div>
            </div>
            <div>
              <h3 className="job-info-title">Profil recherché</h3>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  fontSize: '16px',
                  color: '#2B3E7C',
                }}
              >
                <span>
                  <Suitcase size={18} style={{ marginRight: 8 }} />
                  {job.experience?.length &&
                    experienceOptions(t)[job.experience[0]]}
                </span>
                <span>
                  <GraduationCap size={18} style={{ marginRight: 8 }} />
                  {job.schoolLevel?.length &&
                    schoolLevelsI18(t)[Math.min(...job.schoolLevel)]}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="card-description-title">
          <p>Description</p>
        </div>

        {job?.description?.length > 250 ? (
          <div className="card-description">
            <div
              className="description-preview"
              dangerouslySetInnerHTML={createMarkup(job?.description)}
            />

            <div className="see-more-button">
              <Button light color="secondary" auto onClick={handler}>
                Voir plus
                <ArrowRight size={16} color="#6e2dc0" weight="bold" />
              </Button>
            </div>
          </div>
        ) : (
          <div
            className="description-preview"
            dangerouslySetInnerHTML={createMarkup(job?.description)}
          />
        )}

        <div>
          <Modal
            closeButton
            blur
            width="736px"
            aria-labelledby="modal-title"
            open={visible}
            onClose={closeHandler}
          >
            <Modal.Header>
              {data?.standalone?.topLogoId && (
                <img
                  src={data.standalone.topLogoId}
                  alt="logo company"
                  width={80}
                />
              )}
              <Text id="modal-title" size={18} b>
                {job?.displayName || job?.name}
              </Text>
            </Modal.Header>
            <Modal.Body>
              <Collapse.Group splitted accordion={false}>
                {job?.description && (
                  <Collapse title="Description" expanded>
                    <div className="card-description">
                      <div
                        dangerouslySetInnerHTML={createMarkup(job?.description)}
                      />
                    </div>
                  </Collapse>
                )}

                {job?.clientDescription && (
                  <Collapse title="Entreprise">
                    <div className="card-description">
                      <div
                        dangerouslySetInnerHTML={createMarkup(
                          job?.clientDescription,
                        )}
                      />
                    </div>
                  </Collapse>
                )}

                {job?.candidateDescription && (
                  <Collapse title="Profil recherché">
                    <div className="card-description">
                      <div
                        dangerouslySetInnerHTML={createMarkup(
                          job?.candidateDescription,
                        )}
                      />
                    </div>
                  </Collapse>
                )}
              </Collapse.Group>
            </Modal.Body>
          </Modal>
        </div>

        <div>{children}</div>

        {/* {job?.description && (
          <div style={{ margin: '25px 0px' }}>
            <div className="description">
              <div className="fat-line" />
              <strong style={{ fontSize: 19 }}>
                {t('common:jobDescription')}
              </strong>
            </div>
            <div
              style={{ marginTop: 15 }}
              className="preview"
              dangerouslySetInnerHTML={createMarkup(job?.description)}
            />
          </div>
        )} */}
        {/* {job?.candidateDescription && (
          <div style={{ margin: '25px 0px' }}>
            <div className="description">
              <div className="fat-line" />
              <strong style={{ fontSize: 19 }}>
                {t('common:profileNeeded')}
              </strong>
            </div>
            <div
              style={{ marginTop: 15 }}
              className="preview"
              dangerouslySetInnerHTML={createMarkup(job?.candidateDescription)}
            />
          </div>
        )} */}
      </div>
    </s.CardWrapper>
  )
}

CustomCard.propTypes = propTypes
CustomCard.defaultProps = defaultProps

export default CustomCard
